import React, { useState, useEffect } from "react";
import axios from "axios";
import { Pencil, Trash2 } from "lucide-react";
import HorarioPDF from "../components/HorarioPDF";
import HorarioMemo from "../components/HorarioMemo";
import "../css/Home.css";

function Home({ setIsLoggedIn }) {
  const URL_API = process.env.REACT_APP_URL_API || "http://localhost:5000";
  const [formData, setFormData] = useState({
    id: null,
    hora_inicio: "",
    hora_fin: "",
    dia: "",
    asignatura: "",
    id_usuario: "",
    id_aula: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [horarios, setHorarios] = useState([]); // Inicializado como array vacío
  const [maestros, setMaestros] = useState([]); // Inicializado como array vacío
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchHorarios();
    fetchMaestros();
  }, []);

  const fetchHorarios = async () => {
    const token = localStorage.getItem("tokenSession");
    console.log("🔑 Token:", token);
    try {
      const res = await axios.get(URL_API + "crudHorario/horario", {
        headers: {
          Authorization: token,
        },
      });
      setHorarios(res.data); // Usar res.data, no res.results
    } catch (error) {
      console.error(
        "🚨 Error al obtener horarios:",
        error.response?.data || error
      );
      setHorarios([]); // En caso de error, establecer como array vacío
    }
  };

  const fetchMaestros = async () => {
    try {
      const res = await axios.get(URL_API + "crudUsuario/usuarios/tipo/2");
      setMaestros(res.data.results || []); // Usar res.data.results o vacío si no hay datos
    } catch (error) {
      console.error(
        "🚨 Error al obtener maestros:",
        error.response?.data || error
      );
      setMaestros([]); // En caso de error, establecer como array vacío
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("userSession");
    setIsLoggedIn(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const validate = () => {
    const newErrors = {};
    for (const key in formData) {
      if (key !== "id" && !formData[key].trim()) {
        newErrors[key] = "Este campo es obligatorio";
      }
    }
    if (formData.hora_inicio && formData.hora_fin) {
      const inicio = new Date(`1970-01-01T${formData.hora_inicio}:00`);
      const fin = new Date(`1970-01-01T${formData.hora_fin}:00`);
      if (inicio >= fin) {
        newErrors.hora_inicio =
          "La hora de inicio debe ser menor que la hora de fin";
        newErrors.hora_fin =
          "La hora de fin debe ser mayor que la hora de inicio";
      }
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const requestData = {
      ...formData,
      id_usuario: Number(formData.id_usuario),
      id_aula: Number(formData.id_aula),
    };

    try {
      if (isEditing) {
        if (!formData.id) {
          console.error("🚨 Error: No hay un ID válido para editar");
          return;
        }
        await axios.put(
          URL_API + `crudHorario/horario/${formData.id}`,
          requestData
        );
        setIsEditing(false);
      } else {
        await axios.post(URL_API + "crudHorario/horario", requestData);
      }

      setFormData({
        id: null,
        hora_inicio: "",
        hora_fin: "",
        dia: "",
        asignatura: "",
        id_usuario: "",
        id_aula: "",
      });
      setErrors({});
      fetchHorarios();
      setIsModalOpen(false);
    } catch (error) {
      console.error(
        "🚨 Error al guardar horario:",
        error.response?.data || error
      );
      setErrors({ server: "Error al guardar el horario en el servidor" });
    }
  };

  const handleEdit = (horario) => {
    setFormData({
      id: horario.id,
      hora_inicio: horario.hora_inicio,
      hora_fin: horario.hora_fin,
      dia: String(horario.dia),
      asignatura: horario.asignatura,
      id_usuario: String(horario.id_usuario),
      id_aula: String(horario.id_aula),
    });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(URL_API + `crudHorario/horario/${id}`);
      fetchHorarios();
    } catch (error) {
      console.error("🚨 Error al eliminar horario:", error);
    }
  };

  const handleCloseModal = () => {
    setFormData({
      id: null,
      hora_inicio: "",
      hora_fin: "",
      dia: "",
      asignatura: "",
      id_usuario: "",
      id_aula: "",
    });
    setErrors({});
    setIsEditing(false);
    setIsModalOpen(false);
  };

  const diasSemana = [
    { value: "1", label: "Lunes" },
    { value: "2", label: "Martes" },
    { value: "3", label: "Miércoles" },
    { value: "4", label: "Jueves" },
    { value: "5", label: "Viernes" },
  ];

  const aulas = [
    { value: "1", label: "Aula 11" },
    { value: "2", label: "Aula 12" },
    { value: "3", label: "Aula 13" },
    { value: "4", label: "Aula 14" },
    { value: "5", label: "Aula 15" },
    { value: "6", label: "Aula 16" },
    { value: "7", label: "Aula 17" },
    { value: "8", label: "Aula SUMPA" },
    { value: "9", label: "Aula SAVPA" },
  ];

  return (
    <div className="container">
      <div className="header">
        <div className="title-section">
          <h1>Gestión de Horarios</h1>
          <button
            onClick={() => setIsModalOpen(true)}
            className="nuevo-horario-btn"
          >
            Nuevo Horario
          </button>
        </div>
        <div className="filter-section">
          <h2>Filtrar por ID de usuario</h2>
          <HorarioMemo horarios={horarios} maestros={maestros} />
        </div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{isEditing ? "Editar Horario" : "Agregar Nuevo Horario"}</h2>
            <form onSubmit={handleSubmit} className="form">
              <div className="input-group">
                <label>Hora de inicio</label>
                <input
                  type="time"
                  name="hora_inicio"
                  value={formData.hora_inicio}
                  onChange={handleChange}
                  required
                />
                {errors.hora_inicio && (
                  <p className="error-message">{errors.hora_inicio}</p>
                )}
              </div>

              <div className="input-group">
                <label>Hora de fin</label>
                <input
                  type="time"
                  name="hora_fin"
                  value={formData.hora_fin}
                  onChange={handleChange}
                  required
                />
                {errors.hora_fin && (
                  <p className="error-message">{errors.hora_fin}</p>
                )}
              </div>

              <div className="input-group">
                <label>Día</label>
                <select
                  name="dia"
                  value={formData.dia}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione un día</option>
                  {diasSemana.map((dia) => (
                    <option key={dia.value} value={dia.value}>
                      {dia.label}
                    </option>
                  ))}
                </select>
                {errors.dia && <p className="error-message">{errors.dia}</p>}
              </div>

              <div className="input-group">
                <label>Asignatura</label>
                <input
                  type="text"
                  name="asignatura"
                  value={formData.asignatura}
                  onChange={handleChange}
                  required
                />
                {errors.asignatura && (
                  <p className="error-message">{errors.asignatura}</p>
                )}
              </div>

              <div className="input-group">
                <label>Profesor</label>
                <select
                  name="id_usuario"
                  value={formData.id_usuario}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione un profesor</option>
                  {maestros && maestros.length > 0 ? (
                    maestros.map((maestro) => (
                      <option key={maestro.id} value={maestro.id}>
                        {`${maestro.nombre} ${maestro.apellido_paterno} ${maestro.apellido_materno}`}
                      </option>
                    ))
                  ) : (
                    <option value="">No hay maestros disponibles</option>
                  )}
                </select>
                {errors.id_usuario && (
                  <p className="error-message">{errors.id_usuario}</p>
                )}
              </div>

              <div className="input-group">
                <label>Aula</label>
                <select
                  name="id_aula"
                  value={formData.id_aula}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione un aula</option>
                  {aulas.map((aula) => (
                    <option key={aula.value} value={aula.value}>
                      {aula.label}
                    </option>
                  ))}
                </select>
                {errors.id_aula && (
                  <p className="error-message">{errors.id_aula}</p>
                )}
              </div>

              {errors.server && (
                <p className="error-message">{errors.server}</p>
              )}

              <div className="button-group">
                <button type="submit" className="submit-btn">
                  {isEditing ? "Guardar cambios" : "Agregar horario"}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="close-btn"
                >
                  Cerrar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <h2>Lista de Horarios</h2>
      {horarios && horarios.length > 0 ? (
        <table className="horario-table">
          <thead>
            <tr>
              <th>Hora Inicio</th>
              <th>Hora Fin</th>
              <th>Día</th>
              <th>Asignatura</th>
              <th>ID Usuario</th>
              <th>Usuario</th>
              <th>Aula</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {horarios.map((horario) => (
              <tr key={horario.id}>
                <td>{horario.hora_inicio}</td>
                <td>{horario.hora_fin}</td>
                <td>
                  {
                    ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"][
                      horario.dia - 1
                    ]
                  }
                </td>
                <td>{horario.asignatura}</td>
                <td>{horario.id_usuario}</td>
                <td>
                  {horario.usuario_nombre} {horario.usuario_apellido_materno}{" "}
                  {horario.usuario_apellido_paterno}
                </td>
                <td>{horario.aula_nombre}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => handleEdit(horario)}
                      className="action-btn"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(horario.id)}
                      className="delete-btn"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No hay horarios registrados</p>
      )}
    </div>
  );
}

export default Home;
